import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Menu,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import "./../../App.css";
import PersonIcon from "@mui/icons-material/Person";
import { Fragment, useState } from "react";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import { CarePrincipalDTO } from "../../generated/api";
import { useTranslation } from "react-i18next";
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SecurityIcon from '@mui/icons-material/Security';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import { NavLink } from "react-router";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from '@mui/icons-material/Info';

export function UserInfo() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const careprincipals = useSelector(
    (store: any) => store.carePrincipal.carePrincipals
  );
  const currentUser = useSelector((store: any) => store.session.currentUser);
  const prefersDarkMode: string = useSelector(
    (state: any) => state.settings.theme
  );
  const defaultCarePrincipal = useSelector(
    (state: any) => state.session.selectedCarePrincipal
  );

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [openRoles, setOpenRoles] = useState(false);
  const [openCarePrincipal, setOpenCarePrincipal] = useState(false);

  const toggleColorMode = () => {
    const theme = prefersDarkMode === "dark" ? 'light' : 'dark';
    dispatch({ type: "SET_THEME", theme });
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <>
      {currentUser ? (
        <Fragment>
          <Box sx={{ flexGrow: 0 }}>
            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}>
                
              <Stack alignItems="center" sx={{ margin: "0 1vw 0 1vw" }} spacing={1}>
                <Accordion
                  sx={{
                    m: 0,
                    '&:before': {
                      backgroundColor: 'transparent',
                    }
                  }}
                  style={prefersDarkMode === "light" ? { backgroundColor: "#ffffff" } : { backgroundColor: "#2f2f2f" }}
                  disableGutters
                  elevation={0}
                >
                  <AccordionSummary
                    sx={{ ml: '3.5rem', height: "4rem" }}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                    expandIcon={<ExpandMoreIcon />}>
                    <InfoIcon sx={{ height: "3rem", width: '3rem' }} />
                    <Typography sx={{mt:"0.5rem"}} variant='h6'>
                      {t('titles.help.user.info')}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack alignItems="center" sx={{ margin: "0 1vw 0 1vw" }} spacing={1}>
                      <Stack direction="row">
                        <AssuredWorkloadIcon />
                        <Typography sx={{ fontWeight: "bold" }}>{t("careProvider")}</Typography>
                      </Stack>
                      <Typography>
                        {currentUser?.healthCareProviderName +
                          " ( " +
                          currentUser?.healthCareProviderHsaId +
                          " )"}
                      </Typography>
                      <Stack direction="row">
                        <LocalHospitalIcon />
                        <Typography sx={{ fontWeight: "bold" }}>{t("careUnit")}</Typography>
                      </Stack>
                      <Typography>
                        {currentUser?.healthCareUnitName +
                          " ( " +
                          currentUser?.healthCareUnitHsaId +
                          " )"}
                      </Typography>
                      <Stack direction="row">
                        <MedicalInformationIcon />
                        <Typography sx={{ fontWeight: "bold" }}>{t("issuer")}</Typography>
                      </Stack>
                      <Typography>{currentUser?.issuer}</Typography>
                      <Stack direction="row">
                        <SecurityIcon />
                        <Typography sx={{ fontWeight: "bold" }}>{t("loa")}</Typography>
                      </Stack>
                      <Typography>{currentUser?.levelOfAssurance}</Typography>
                      <Stack>
                        <Stack direction="row">
                          <LockPersonIcon />
                          <Typography sx={{ fontWeight: "bold" }}>{t("systemroles")}</Typography>
                          <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => setOpenRoles(!openRoles)}>
                            {openRoles ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </Stack>
                        <Collapse in={openRoles} timeout="auto" unmountOnExit>
                          <List dense={true}>
                            {currentUser ? (
                              currentUser?.systemRoles?.map((role: any, index: number) => (
                                <ListItem key={index}>{role}</ListItem>
                              ))
                            ) : (
                              <ListItem>{t("noSystemroles")}</ListItem>
                            )}
                          </List>
                        </Collapse>
                        <Stack direction="row">
                          <HealthAndSafetyIcon />
                          <Typography sx={{ fontWeight: "bold" }}>{t("careprincipals")}</Typography>
                          <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => setOpenCarePrincipal(!openCarePrincipal)}>
                            {openCarePrincipal ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </Stack>
                        <Collapse in={openCarePrincipal} timeout="auto" unmountOnExit>
                          <List dense={true}>
                            {careprincipals ? (
                              careprincipals?.map((carep: CarePrincipalDTO, index: number) => (
                                <ListItem key={index}>{carep.name}</ListItem>
                              ))
                            ) : (
                              <ListItem>{t("noSystemroles")}</ListItem>
                            )}
                          </List>
                        </Collapse>
                      </Stack>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
                <IconButton
                  onClick={toggleColorMode}
                  color="inherit">
                  {prefersDarkMode === "dark" ? (
                    <Fragment>
                      <Brightness4Icon />
                      <Typography>{t('lightMode')}</Typography>
                    </Fragment>
                  ) : (
                    <Fragment>
                      <Brightness7Icon />
                      <Typography>{t('darkMode')}</Typography>
                    </Fragment>
                  )}
                </IconButton>
                <ListItemButton
                  key="managePerson"
                  component={NavLink}
                  to="/logout">
                  <LogoutIcon />
                  <Typography>{t('titles.help.logout')}</Typography>
                </ListItemButton>
              </Stack>
            </Menu>
            <Tooltip title="Öppna inställningar">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <PersonIcon sx={{ height: '3rem', width: '3rem' }} />
              </IconButton>
            </Tooltip>
            <Typography >{currentUser?.name}: {currentUser?.employeeHsaId}{" "}</Typography>
            <Typography >{defaultCarePrincipal?.name}</Typography>
          </Box>
        </Fragment>
      ) : null
      }
    </>
  );
}
